/**
 * @file `<wlsn-scrolling-bar>` — infinite marquee / ticker built with cloned items + `requestAnimationFrame`.
 *
 * ## Markup
 * - Host: `<wlsn-scrolling-bar>` with required `data-allow-animation="true"` or the script never runs.
 * - One child **track**: a div with class `js-wlsn-scrolling-bar-track` (and usually `wlsn-scrolling-bar__track`).
 * - **Items**: direct children of the track (e.g. `.wlsn-scrolling-bar__item`). **At least 2 items** are required
 *   for infinite scroll; with 0–1 items the component falls back to static layout.
 *
 * ## Data attributes (all optional except where noted)
 * | Attribute | Role |
 * |-----------|------|
 * | `data-allow-animation="true"` | **Required** to enable the custom element (constructor bails otherwise). |
 * | `data-speed-desk` / `data-speed-tab` / `data-speed-mob` | Scroll speed (px/s) per breakpoint. Defaults: 40. Tablet: 768–1023px, mobile: &lt;768px, desktop: ≥1024px. |
 * | `data-direction-desk` / `data-direction-tab` / `data-direction-mob` | `rtl` \| `ltr` (horizontal) or `btt` \| `ttb` (vertical). Default horizontal: `rtl`. |
 * | `data-container-height-desk` / `-tab` / `-mob` | Visible height (px) for **vertical** axis; sets host `min-height`. Default 300. |
 * | `data-pause-on-hover-desk="true"` | Pause on hover on **desktop only** (≥1024px). |
 * | `data-force-scroll="true"` | Always run marquee when there are 2+ items, even if content fits (skips overflow checks). |
 *
 * ## Behaviour
 * - **Overflow**: By default, horizontal scroll runs only if content is wider than the host; vertical only if
 *   content is taller than the configured container height. If it fits → **fallback** (no clones, no RAF).
 * - **`data-force-scroll="true"`**: Forces infinite scroll whenever overflow would have skipped it (still needs 2+ items).
 * - **`prefers-reduced-motion: reduce`**: Animation does not run (speed treated as 0).
 * - **Resize / viewport**: `ResizeObserver`, window resize, orientation change, and `IntersectionObserver` trigger
 *   `rebuild()` when needed; tab visibility pauses/resumes.
 *
 * ## CSS hooks (classes on `<wlsn-scrolling-bar>`)
 * - `wlsn-scrolling-bar--scrolling` — clones are active and the bar is animating.
 * - `wlsn-scrolling-bar--fallback` — static layout (fits container, single item, or non-animated case from build).
 * - Track modifier: `wlsn-scrolling-bar__track--vertical` for vertical directions.
 * - Cloned nodes get `.is-clone` and `aria-hidden="true"`.
 *
 * ## Optional parent layout (marquee sections)
 * Wrap content in an element with class `wlsn-scrolling-bar-parent`. When scroll is active, the host toggles
 * `wlsn-scrolling-bar-parent--scroll-active` on that ancestor (e.g. switch inner layout to flex column without `:has()`).
 *
 * ## Public API (from JS)
 * - `element.rebuild()` — tear down clones and measure again (after images/layout change).
 * - `element.pause()` / `element.resume()` — animation control (respects reduced motion and `built` state).
 * - `element.onResize()` — forwards to internal debounced resize handler.
 *
 * ## Examples
 *
 * **Horizontal marquee (RTL), speeds per breakpoint:**
 * ```html
 * <wlsn-scrolling-bar
 *   data-allow-animation="true"
 *   data-speed-desk="40"
 *   data-speed-tab="40"
 *   data-speed-mob="24"
 *   data-direction-desk="rtl"
 *   data-direction-tab="rtl"
 *   data-direction-mob="rtl"
 * >
 *   <div class="wlsn-scrolling-bar__track js-wlsn-scrolling-bar-track" role="list">
 *     <div class="wlsn-scrolling-bar__item" role="listitem">…</div>
 *     <div class="wlsn-scrolling-bar__item" role="listitem">…</div>
 *   </div>
 * </wlsn-scrolling-bar>
 * ```
 *
 * **Always animate (even if all logos fit in one row):**
 * ```html
 * <wlsn-scrolling-bar
 *   data-allow-animation="true"
 *   data-force-scroll="true"
 *   data-speed-desk="40"
 * >
 *   <div class="wlsn-scrolling-bar__track js-wlsn-scrolling-bar-track" role="list">…</div>
 * </wlsn-scrolling-bar>
 * ```
 *
 * **Vertical column (e.g. images), bottom-to-top:**
 * ```html
 * <wlsn-scrolling-bar
 *   data-allow-animation="true"
 *   data-direction-desk="btt"
 *   data-container-height-desk="420"
 *   data-speed-desk="40"
 * >
 *   <div class="wlsn-scrolling-bar__track js-wlsn-scrolling-bar-track" role="list">…</div>
 * </wlsn-scrolling-bar>
 * ```
 *
 * **Section with layout hook (see `wlsn-marquee.liquid`):**
 * ```html
 * <div class="wlsn-marquee__inner wlsn-section__inner wlsn-scrolling-bar-parent">
 *   <wlsn-scrolling-bar class="wlsn-marquee__scrolling-bar" data-allow-animation="true" …>
 *     <div class="wlsn-scrolling-bar__track js-wlsn-scrolling-bar-track">…</div>
 *   </wlsn-scrolling-bar>
 * </div>
 * ```
 * Style `.wlsn-scrolling-bar-parent--scroll-active` (e.g. in `wlsn-marquee.css`) when the marquee is running.
 */

if (!customElements.get('wlsn-scrolling-bar')) {
  class WLSScrollingBar extends HTMLElement {
    constructor() {
      super();

      const n = (v, fb = 40) => {
        const num = Number(v);
        return Number.isFinite(num) && num >= 0 ? num : fb;
      };
      this.scrollSpeedDesktop = n(this.getAttribute('data-speed-desk'), 40);
      this.scrollSpeedTablet = n(this.getAttribute('data-speed-tab'), 40);
      this.scrollSpeedMobile = n(this.getAttribute('data-speed-mob'), 40);

      // rtl | ltr | btt | ttb (legacy: x -> rtl, y -> btt)
      const dir = (v) => {
        const s = (v || 'rtl').toLowerCase();
        if (s === 'ltr' || s === 'ttb') return s;
        if (s === 'btt') return 'btt';
        if (s === 'y') return 'btt';
        return 'rtl';
      };
      this.directionDesktop = dir(this.getAttribute('data-direction-desk'));
      this.directionTablet = dir(this.getAttribute('data-direction-tab'));
      this.directionMobile = dir(this.getAttribute('data-direction-mob'));

      const ch = (v, fb) => {
        const num = Number(v);
        return Number.isFinite(num) && num > 0 ? num : fb;
      };
      this.containerHeightDesktop = ch(this.getAttribute('data-container-height-desk'), 300);
      this.containerHeightTablet = ch(this.getAttribute('data-container-height-tab'), 300);
      this.containerHeightMobile = ch(this.getAttribute('data-container-height-mob'), 300);

      this.allowAnimation = String(this.getAttribute('data-allow-animation')).toLowerCase() === 'true';
      this.pauseOnHoverDesktop = String(this.getAttribute('data-pause-on-hover-desk')).toLowerCase() === 'true';

      this.classes = {
        track: 'js-wlsn-scrolling-bar-track',
        vertical: 'wlsn-scrolling-bar__track--vertical',
        fallback: 'wlsn-scrolling-bar--fallback',
        scrolling: 'wlsn-scrolling-bar--scrolling',
        scrollingParent: 'wlsn-scrolling-bar-parent',
      };
      this.state = {
        rafId: 0,
        lastTs: null,
        offset: 0,
        wrapSize: 0,
        axis: 'x',
        direction: 'rtl',
        originalItemCount: 0,
        paused: false,
        built: false,
        triedAutoStart: false,
      };

      this._intersectionObserver = null;
      this._resizeObserver = null;
      this._mqlDesktop = window.matchMedia('(min-width: 1024px)');
      this._mqlTablet = window.matchMedia('(min-width: 768px) and (max-width: 1023.98px)');
      this._mqlMobile = window.matchMedia('(max-width: 767.98px)');
      this._mqlReduced = window.matchMedia('(prefers-reduced-motion: reduce)');

      this._onVisChange = this._onVisChange.bind(this);
      this._onMqlChange = this._onMqlChange.bind(this);
      this._onResize = this._debounce(() => this.rebuild(), 120);
      this._onIntersect = this._onIntersect.bind(this);
      this._autoStart = this._autoStart.bind(this);
      this._onMouseEnter = this._onMouseEnter.bind(this);
      this._onMouseLeave = this._onMouseLeave.bind(this);
    }

    /**
     * Get DOM elements
     * @returns {object} - Object containing DOM element references
     */
    get elements() {
      return { track: this.querySelector(`.${this.classes.track}`) };
    }

    /**
     * When true (attribute data-force-scroll="true"), always run infinite scroll if there are 2+ items,
     * even when content fits the container (skips overflow checks).
     * @returns {boolean}
     */
    _getForceScroll() {
      return String(this.getAttribute('data-force-scroll')).toLowerCase() === 'true';
    }

    /**
     * Connected callback lifecycle method
     * @returns {void}
     */
    connectedCallback() {
      if (!this.allowAnimation) return;
      const { track } = this.elements;
      if (!track) return;

      track.style.willChange = 'transform';

      // Don't hang on images forever
      this._waitForImages(this, 1200).then(() => this._build());

      document.addEventListener('visibilitychange', this._onVisChange);
      this._mqlDesktop.addEventListener('change', this._onMqlChange);
      this._mqlTablet.addEventListener('change', this._onMqlChange);
      this._mqlMobile.addEventListener('change', this._onMqlChange);
      this._mqlReduced.addEventListener('change', this._onMqlChange);

      this._intersectionObserver = new IntersectionObserver(this._onIntersect, { root: null, threshold: 0 });
      this._intersectionObserver.observe(this);

      this._resizeObserver = new ResizeObserver(this._onResize);
      this._resizeObserver.observe(this);

      window.addEventListener('resize', this._onResize, { passive: true });
      // Mobile-specific: handle orientation change
      window.addEventListener(
        'orientationchange',
        () => {
          setTimeout(() => this.rebuild(), 100);
        },
        { passive: true }
      );

      // Setup hover listeners for desktop
      if (this.pauseOnHoverDesktop) {
        this.addEventListener('mouseenter', this._onMouseEnter, { passive: true });
        this.addEventListener('mouseleave', this._onMouseLeave, { passive: true });
      }

      // Safety autostart: increased timeout for slower mobile devices
      setTimeout(this._autoStart, 1500);
    }

    /**
     * Disconnected callback lifecycle method
     * @returns {void}
     */
    disconnectedCallback() {
      this._teardown();
      document.removeEventListener('visibilitychange', this._onVisChange);
      window.removeEventListener('resize', this._onResize);
      window.removeEventListener('orientationchange', this._rebuild);

      this._mqlDesktop.removeEventListener('change', this._onMqlChange);
      this._mqlTablet.removeEventListener('change', this._onMqlChange);
      this._mqlMobile.removeEventListener('change', this._onMqlChange);
      this._mqlReduced.removeEventListener('change', this._onMqlChange);

      if (this.pauseOnHoverDesktop) {
        this.removeEventListener('mouseenter', this._onMouseEnter);
        this.removeEventListener('mouseleave', this._onMouseLeave);
      }

      if (this._intersectionObserver) {
        this._intersectionObserver.disconnect();
        this._intersectionObserver = null;
      }
      if (this._resizeObserver) {
        this._resizeObserver.disconnect();
        this._resizeObserver = null;
      }
    }

    /**
     * Pause scrolling animation
     * @returns {void}
     */
    pause() {
      if (this.state.paused) return;
      this.state.paused = true;
      if (this.state.rafId) cancelAnimationFrame(this.state.rafId);
      this.state.rafId = 0;
    }

    /**
     * Resume scrolling animation
     * @returns {void}
     */
    resume() {
      if (!this.allowAnimation || this._mqlReduced.matches || !this.state.built) return;
      if (this.state.paused) {
        this.state.paused = false;
        this.state.lastTs = null;
        if (this._getScrollSpeed() > 0) {
          this.state.rafId = requestAnimationFrame((ts) => this._step(ts));
        }
      }
    }

    /**
     * Handle resize event (public method for external use)
     * @returns {void}
     */
    onResize() {
      this._onResize();
    }

    /**
     * Toggle parent section inner layout (flex col when infinite scroll runs). No :has() — works in older browsers.
     * @returns {void}
     */
    _syncParentInnerLayoutClass() {
      const parent = this.closest(`.${this.classes.scrollingParent}`);
      if (!parent) return;
      parent.classList.toggle(`${this.classes.scrollingParent}--scroll-active`, this.classList.contains(this.classes.scrolling));
    }

    /**
     * Rebuild scrolling bar (public method for external use)
     * @returns {void}
     */
    rebuild() {
      this._teardown();
      // Increased timeout for mobile devices with slower connections
      this._waitForImages(this, 1500).then(() => this._build());
    }

    /**
     * Build scrolling bar with clones
     * @returns {void}
     */
    _build() {
      const track = this.elements.track;
      if (!track) return;

      this._removeClones(track);
      this.classList.remove(this.classes.fallback, this.classes.scrolling);
      this._syncParentInnerLayoutClass();
      track.classList.remove(this.classes.vertical);
      this.style.minHeight = '';
      this.state.offset = 0;
      this.state.lastTs = null;

      const direction = this._getDirection();
      this.state.direction = direction;
      this.state.axis = direction === 'btt' || direction === 'ttb' ? 'y' : 'x';

      const originals = Array.from(track.children);
      if (originals.length <= 1) {
        this._exitBuildNoScroll(track);
        return;
      }

      if (this.state.axis === 'y') {
        track.classList.add(this.classes.vertical);
        const containerHeight = this._getContainerHeight();
        this.style.minHeight = `${containerHeight}px`;

        const baseHeight = this._measureBaseHeightOriginals(track, originals.length);
        if (baseHeight <= 0) {
          this._exitBuildNoScroll(track);
          return;
        }

        // Scroll only when original column overflows the container height (unless data-force-scroll="true")
        if (!this._getForceScroll() && containerHeight > 0 && baseHeight <= containerHeight) {
          this._exitBuildNoScroll(track);
          return;
        }

        const buffer = 20;
        const minCoverage = containerHeight + baseHeight + buffer;
        const setsNeeded = Math.max(2, Math.ceil(minCoverage / baseHeight));
        this._prependCloneSets(track, originals, 1);
        this._appendCloneSets(track, originals, setsNeeded - 1);

        this.state.originalItemCount = originals.length;
        this.state.wrapSize = baseHeight;
        this.state.built = true;
        this.classList.add(this.classes.scrolling);
        track.style.willChange = 'transform';
      } else {
        const baseWidth = this._measureBaseWidthOriginals(track, originals.length);
        if (baseWidth <= 0) {
          this._exitBuildNoScroll(track);
          return;
        }

        const containerWidth = this.clientWidth || track.clientWidth || window.innerWidth || 0;
        // Scroll only when originals overflow the host width (unless data-force-scroll="true")
        if (!this._getForceScroll() && (containerWidth <= 0 || baseWidth <= containerWidth)) {
          this._exitBuildNoScroll(track);
          return;
        }

        const buffer = window.innerWidth < 768 ? 100 : 2;
        const minCoverage = containerWidth + baseWidth + buffer;
        const setsNeeded = Math.max(2, Math.ceil(minCoverage / baseWidth));
        this._prependCloneSets(track, originals, 1);
        this._appendCloneSets(track, originals, setsNeeded - 1);

        this.state.originalItemCount = originals.length;
        this.state.wrapSize = baseWidth;
        this.state.built = true;
        this.classList.add(this.classes.scrolling);
        track.style.willChange = 'transform';
      }

      this._syncParentInnerLayoutClass();

      const speed = this._getScrollSpeed();
      if (!this._mqlReduced.matches && speed > 0) {
        this.state.rafId = requestAnimationFrame((ts) => this._step(ts));
      }
    }

    /**
     * No infinite scroll: static layout, host gets fallback class for CSS (centering, wrap, etc.)
     * @param {HTMLElement} track - The track element
     * @returns {void}
     */
    _exitBuildNoScroll(track) {
      this.state.built = false;
      track.style.transform = 'translate3d(0, 0, 0)';
      track.style.willChange = '';
      this.classList.remove(this.classes.scrolling);
      this.classList.add(this.classes.fallback);
      this._syncParentInnerLayoutClass();
    }

    /**
     * Teardown scrolling bar by cleaning up
     * @returns {void}
     */
    _teardown() {
      if (this.state.rafId) cancelAnimationFrame(this.state.rafId);
      this.state.rafId = 0;
      this.state.lastTs = null;
      this.state.offset = 0;

      const track = this.elements.track;
      if (track) {
        this._removeClones(track);
        track.style.transform = 'translate3d(0, 0, 0)';
        track.classList.remove(this.classes.vertical);
      }
      this.style.minHeight = '';
      this.state.built = false;
      this.classList.remove(this.classes.fallback, this.classes.scrolling);
      this._syncParentInnerLayoutClass();
    }

    /**
     * Animation step function
     * @param {number} timestamp - The animation frame timestamp
     * @returns {void}
     */
    _step(timestamp) {
      if (this.state.paused || !this.state.built) return;

      const speed = this._getScrollSpeed();
      if (speed <= 0 || this._mqlReduced.matches) {
        this.pause();
        return;
      }

      if (this.state.lastTs == null) this.state.lastTs = timestamp;
      const dt = (timestamp - this.state.lastTs) / 1000;
      this.state.lastTs = timestamp;

      this.state.offset += speed * dt;
      if (this.state.offset >= this.state.wrapSize) this.state.offset -= this.state.wrapSize;

      const dpr = Math.max(1, window.devicePixelRatio || 1);
      const snapped = Math.round(this.state.offset * dpr) / dpr;
      const d = this.state.direction;
      const wrap = this.state.wrapSize;
      // LTR: start at -wrapSize (show originals), animate to 0 (show clones), then wrap
      const tx = d === 'ltr' ? snapped - wrap : d === 'rtl' ? -snapped : 0;
      const ty = d === 'ttb' ? snapped : d === 'btt' ? -snapped : 0;

      const { track } = this.elements;
      if (track) track.style.transform = `translate3d(${tx}px, ${ty}px, 0)`;

      this.state.rafId = requestAnimationFrame((nextTs) => this._step(nextTs));
    }

    /**
     * Get scroll speed based on breakpoint
     * @returns {number} - Scroll speed in pixels per second
     */
    _getScrollSpeed() {
      if (this._mqlReduced.matches) return 0;
      if (this._mqlDesktop.matches) return this.scrollSpeedDesktop;
      if (this._mqlTablet.matches) return this.scrollSpeedTablet;
      if (this._mqlMobile.matches) return this.scrollSpeedMobile;
      const w = window.innerWidth || document.documentElement.clientWidth || 0;
      if (w >= 1024) return this.scrollSpeedDesktop;
      if (w >= 768) return this.scrollSpeedTablet;
      return this.scrollSpeedMobile;
    }

    /**
     * Get scroll direction based on breakpoint
     * @returns {'rtl'|'ltr'|'btt'|'ttb'}
     */
    _getDirection() {
      if (this._mqlDesktop.matches) return this.directionDesktop;
      if (this._mqlTablet.matches) return this.directionTablet;
      if (this._mqlMobile.matches) return this.directionMobile;
      const w = window.innerWidth || document.documentElement.clientWidth || 0;
      if (w >= 1024) return this.directionDesktop;
      if (w >= 768) return this.directionTablet;
      return this.directionMobile;
    }

    /**
     * Get container height for vertical scroll based on breakpoint
     * @returns {number}
     */
    _getContainerHeight() {
      if (this._mqlDesktop.matches) return this.containerHeightDesktop;
      if (this._mqlTablet.matches) return this.containerHeightTablet;
      if (this._mqlMobile.matches) return this.containerHeightMobile;
      const w = window.innerWidth || document.documentElement.clientWidth || 0;
      if (w >= 1024) return this.containerHeightDesktop;
      if (w >= 768) return this.containerHeightTablet;
      return this.containerHeightMobile;
    }

    // ---- Coverage helpers ----

    /**
     * Append clone sets to track for infinite scrolling
     * @param {HTMLElement} track - The track element
     * @param {Array<HTMLElement>} originals - Original items to clone
     * @param {number} setsToAdd - Number of sets to add
     * @returns {void}
     */
    _appendCloneSets(track, originals, setsToAdd) {
      if (setsToAdd <= 0) return;
      const frag = document.createDocumentFragment();
      for (let s = 0; s < setsToAdd; s++) {
        for (const el of originals) {
          const clone = el.cloneNode(true);
          clone.classList.add('is-clone');
          clone.setAttribute('aria-hidden', 'true');
          frag.appendChild(clone);
        }
      }
      track.appendChild(frag);
    }

    /**
     * Prepend clone sets to track (for LTR: content before originals)
     * @param {HTMLElement} track - The track element
     * @param {Array<HTMLElement>} originals - Original items to clone
     * @param {number} setsToAdd - Number of sets to add
     * @returns {void}
     */
    _prependCloneSets(track, originals, setsToAdd) {
      if (setsToAdd <= 0) return;
      const frag = document.createDocumentFragment();
      for (let s = 0; s < setsToAdd; s++) {
        for (const el of originals) {
          const clone = el.cloneNode(true);
          clone.classList.add('is-clone');
          clone.setAttribute('aria-hidden', 'true');
          frag.appendChild(clone);
        }
      }
      track.insertBefore(frag, track.firstChild);
    }

    /**
     * Measure base width of original items
     * @param {HTMLElement} track - The track element
     * @param {number} originalCount - Number of original items
     * @returns {number} - The measured width
     */
    _measureBaseWidthOriginals(track, originalCount) {
      const children = track.children;
      if (!children || children.length < originalCount) return 0;
      const first = children[0];
      const last = children[originalCount - 1];
      if (!first || !last) return 0;

      const r1 = first.getBoundingClientRect();
      const r2 = last.getBoundingClientRect();
      const w = r2.right - r1.left;
      return Number.isFinite(w) && w > 0 ? w : 0;
    }

    /**
     * Measure base height of original items (including gap) for vertical scroll
     * @param {HTMLElement} track - The track element
     * @param {number} originalCount - Number of original items
     * @returns {number} - The measured height
     */
    _measureBaseHeightOriginals(track, originalCount) {
      const children = track.children;
      if (!children || children.length < originalCount) return 0;
      const first = children[0];
      const last = children[originalCount - 1];
      if (!first || !last) return 0;

      const r1 = first.getBoundingClientRect();
      const r2 = last.getBoundingClientRect();
      const h = r2.bottom - r1.top;
      return Number.isFinite(h) && h > 0 ? h : 0;
    }

    /**
     * Remove all cloned elements from track
     * @param {HTMLElement} track - The track element
     * @returns {void}
     */
    _removeClones(track) {
      track.querySelectorAll('.is-clone').forEach((n) => n.remove());
    }

    // Don't block: with timeout; don't use decode for lazy until it enters viewport
    /**
     * Wait for images to load with timeout
     * @param {HTMLElement} scope - The scope element to search for images
     * @param {number} [timeoutMs=1200] - Timeout in milliseconds
     * @returns {Promise<void>}
     */
    _waitForImages(scope, timeoutMs = 1200) {
      const imgs = Array.from(scope.querySelectorAll('img'));
      if (imgs.length === 0) return Promise.resolve();

      const waiters = imgs.map((img) => {
        // if it has dimensions or is complete → done
        if (img.complete && (img.naturalWidth || img.width)) return Promise.resolve();

        // For lazy-loaded images, force load by checking loading attribute
        if (img.loading === 'lazy') {
          img.loading = 'eager';
        }

        // decode can hang for lazy; we use load/error fallback
        return new Promise((res) => {
          const done = () => {
            img.removeEventListener('load', done);
            img.removeEventListener('error', done);
            res();
          };
          img.addEventListener('load', done, { once: true, passive: true });
          img.addEventListener('error', done, { once: true, passive: true });

          // Trigger load if src exists but hasn't started loading
          if (img.src && !img.complete) {
            img.src = img.src;
          }
        });
      });

      const all = Promise.allSettled(waiters).then(() => {});
      const timeout = new Promise((res) => setTimeout(res, timeoutMs));
      // whichever first: don't wait indefinitely
      return Promise.race([all, timeout]);
    }

    /**
     * Debounce helper function
     * @param {Function} fn - Function to debounce
     * @param {number} [delayMs=120] - Delay in milliseconds
     * @returns {Function} - Debounced function
     */
    _debounce(fn, delayMs = 120) {
      let id;
      return (...args) => {
        clearTimeout(id);
        id = setTimeout(() => fn(...args), delayMs);
      };
    }

    // Listeners ----
    /**
     * Handle visibility change event
     * @returns {void}
     */
    _onVisChange() {
      if (document.hidden) this.pause();
      else this.resume();
    }

    /**
     * Handle media query change event
     * @returns {void}
     */
    _onMqlChange() {
      if (this._mqlReduced.matches || this._getScrollSpeed() <= 0) this.pause();
      else this.resume();
    }

    /**
     * Handle intersection observer callback
     * @param {Array<IntersectionObserverEntry>} entries - Intersection observer entries
     * @returns {void}
     */
    _onIntersect(entries) {
      const e = entries[0];
      if (!e) return;
      if (e.isIntersecting) {
        // If it appears for the first time and is not built (was display:none), try rebuild
        // Add small delay on mobile to ensure viewport has settled (iOS Safari issue)
        if (!this.state.built) {
          const isMobile = window.innerWidth < 768;
          if (isMobile) {
            setTimeout(() => this.rebuild(), 150);
          } else {
            this.rebuild();
          }
        } else {
          this.resume();
        }
      } else {
        this.pause();
      }
    }

    /**
     * Auto start fallback function
     * @returns {void}
     */
    _autoStart() {
      // Safety fallback: if it's still not built (e.g. images/layout blocked)
      if (!this.state.built && !this.state.triedAutoStart) {
        this.state.triedAutoStart = true;
        this.rebuild();
      }
    }

    /**
     * Handle mouse enter event (desktop only)
     * @returns {void}
     */
    _onMouseEnter() {
      // Only pause on desktop (width > 1023px)
      if (this.pauseOnHoverDesktop && this._mqlDesktop.matches) {
        this.pause();
      }
    }

    /**
     * Handle mouse leave event (desktop only)
     * @returns {void}
     */
    _onMouseLeave() {
      // Only resume on desktop (width > 1023px)
      if (this.pauseOnHoverDesktop && this._mqlDesktop.matches) {
        this.resume();
      }
    }
  }

  customElements.define('wlsn-scrolling-bar', WLSScrollingBar);
}
